// src/sentry.client.ts
import * as Sentry from "@sentry/react";
import { bucketFromUrl, spanName, datesVariantSuffix } from "@/lib/telemetry/bucket";

console.info("[Sentry] DSN present?", !!import.meta.env.VITE_SENTRY_DSN);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // Performance capture:
    tracesSampleRate: 1.0,               // dev: 1.0; prod: e.g. 0.1
    profilesSampleRate: 0.5,             // optional: JS profiling

    // Make sure fetch/xhr/nav are instrumented
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentNavigation: true,
        instrumentPageLoad: true,
        instrumentFetch: true,
        instrumentXHR: true,
      }),
    ],

    // Only inject trace headers to your own origins:
    tracePropagationTargets: [
      /^https?:\/\/([a-z0-9-]+\.)*boona\.app\b/i,       // any subdomain of boona.app
      /^https?:\/\/127\.0\.0\.1(?::\d+)?\b/i,           // localhost
    ],
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE || undefined
    ,
    // Normalize auto-instrumented fetch/xhr spans, too
    beforeSendTransaction(event) {
      try {
        const spans = Array.isArray((event as any).spans) ? (event as any).spans : [];
        for (const s of spans) {
          const op = s.op || "";
          const isHttp =
            op === "resource.fetch" || op === "resource.xhr" || op === "http.client" || op === "http";
          if (!isHttp) continue;

          const method =
            (s.data && (s.data["http.method"] || s.data.method)) ||
            (typeof s.description === "string" ? s.description.split(" ")[0] : "GET");
          const url =
            (s.data && (s.data.url || s.data["http.url"])) ||
            (typeof s.description === "string" ? s.description.split(" ")[1] : "");
          if (!url) continue;

          const bucket = bucketFromUrl(url);
          const { suffix, count } = datesVariantSuffix(url, bucket);
          s.description = spanName(method, bucket) + suffix; // e.g., "GET company/timetable/schedule (dates:3)"
          s.data = {
            ...(s.data || {}),
            "http.route": bucket,
            "telemetry.bucket": bucket,
            ...(count !== undefined ? { "telemetry.dates.count": count } : {}),
          };
        }
      } catch {
        // never block sending on normalization issues
      }
      return event;
    }
  });
}

declare global { interface Window { __GEO__?: { country?: string } } }
if (window.__GEO__?.country) Sentry.setTag("country", window.__GEO__?.country);
Sentry.setTag("user_agent", navigator.userAgent);
